import 'rc-slider/assets/index.css';

import { Form, Formik } from 'formik';
import { useRouter } from 'next/router';
import ChariotConnect from 'react-chariot-connect';
import { Button } from '~/components/button';
import { Box, Grid } from '~/components/layout';
import { SelectField, SelectProps } from '~/components/select';
import { Text } from '~/components/typography';
import { routes } from '~/config/routes';
import { PageBySlugQuery } from '~/graphql/page-by-slug';
import {
  DonationRecurrenceInterval,
  DonationTier,
  PageStatus
} from '~/types/graphql';
import { FocusError } from '~/utils/focus-error';
import { isEmpty } from '~/utils/is-empty';
import { getRecurringIntervalOptions } from '~/utils/order-utils';

import { useMemo } from 'react';
import LockIcon from './assets/lock-icon.svg';
import { useForm } from './hooks/use-form';
import {
  AmountButton,
  AmountButtonsWrapper,
  AmountInputField,
  ButtonBody,
  SelectDonationSectionWrapper,
  SubmitBtnWrapper
} from './style';
import { WORKSPACE_IDS } from '~/config/workspaces';
import { parseDescription } from '~/pages/fundraising-page/components/donation-tiers';

type Props = {
  page: PageBySlugQuery['pageBySlug'];
  onSubmit: () => void;
  isUsingChariot?: boolean;
};

const options: SelectProps<DonationRecurrenceInterval>['options'] = getRecurringIntervalOptions([
  DonationRecurrenceInterval.TwiceAYear
]);

export type ApplyOptions = 'default';

export const SelectDonation = (props: Props) => {
  const { query } = useRouter();
  const { onSubmit, page, isUsingChariot = false } = props;
  const { slug, status, allowCustomAmount, defaultDonationRecurrenceInterval } =
    page;
  const closed = status === PageStatus.Closed;

  const pageTiers: Array<
    Omit<DonationTier, 'description'> & { description: React.ReactNode }
  > = [];
  const router = useRouter();

  const isProsperityRaffle = useMemo(
    () =>
      page.nonprofit.webId === WORKSPACE_IDS.PROSPERITY_INITIATIVE &&
      page.id === 'pg_tGbumG0j4',
    [page.nonprofit.webId, page.id]
  );

  const isPlayMarinEvent = useMemo(
      () =>
        page.nonprofit.webId === WORKSPACE_IDS.PLAY_MARIN &&
        page.id === 'pg_VvBoCUr3_',
      [page.nonprofit.webId, page.id]
    );

  // TODO useMemo but also make it DRY with Embeddable form
  let sliceEndIndex = -4

  if (isProsperityRaffle) {
    sliceEndIndex = -5
  }

  if (isPlayMarinEvent) {
    page.donationTiers.slice(0,4).map(tier => {
      return pageTiers.push({
        id: `$${(tier.amount / 100).toLocaleString('en')}`,
        amount: tier.amount,
        recurrenceInterval: DonationRecurrenceInterval.None,
        description: parseDescription(tier.description),
        isDefault: tier.isDefault
      });
    });
  } else {
    page.donationTiers.slice(sliceEndIndex).map(tier => {
      return pageTiers.push({
        id: `$${(tier.amount / 100).toLocaleString('en')}`,
        amount: tier.amount,
        recurrenceInterval: DonationRecurrenceInterval.None,
        description: parseDescription(tier.description),
        isDefault: tier.isDefault
      });
    });
  }


  if (allowCustomAmount && !isEmpty(pageTiers)) {
    pageTiers.push({
      id: 'Custom',
      amount: 0,
      recurrenceInterval: DonationRecurrenceInterval.None,
      description: isProsperityRaffle
        ? 'Custom donation amount without raffle tickets.'
        : '',
      isDefault: false
    });
  }

  const frequencyOptions = useMemo(() => {
    // adjustment for equal pride
    if (page.nonprofit.webId === WORKSPACE_IDS.THE_ADVOCATE) {
      return options.filter(
        option =>
          option.value !== DonationRecurrenceInterval.Quarterly &&
          option.value !== DonationRecurrenceInterval.Yearly
      );
    }

    // enable only one-time for the raffle
    if (isProsperityRaffle) {
      return [
        {
          label: 'One-Time',
          value: DonationRecurrenceInterval.None
        }
      ];
    }

    return options;
  }, [page.nonprofit.webId, isProsperityRaffle]);

  const showPaymentFrequency = useMemo(() => {
    return !isProsperityRaffle;
  }, [isProsperityRaffle]);

  const onDonationRequest = (donationAmount: number) => {
    const donationRequestData = {
      amount: donationAmount > 0 ? donationAmount : undefined,
      metadata: {
        fundraiserPageName: page.title
      }
    };

    return donationRequestData;
  };
  const onSuccess = (e: any) => {
    const detail = e.detail;
    const grantIntent = e.detail.grantIntent;
    const chariotSessionData = {
      workflowSessionId: detail.workflowSessionId,
      userFriendlyId: grantIntent.userFriendlyId,
      fundId: grantIntent.fundId,
      amount: grantIntent.amount
    };

    sessionStorage.setItem(
      'chariotSessionData',
      JSON.stringify(chariotSessionData)
    );
    router.push(
      `${routes.donationPage(slug!).as}?chariot=${
        chariotSessionData.workflowSessionId
      }`
    );
  };
  const onExit = () => null;

  const { schema } = useForm();

  const getData = () => {
    let initialData = {
      amount: 0,
      paymentFrequency: defaultDonationRecurrenceInterval
    };

    const defaultTier =
      page.donationTiers.find(tier => tier.isDefault) ?? undefined;

    // disabled for now - some of our customers are confused by this behavior
    // if (typeof window !== 'undefined' && sessionStorage?.formData) {
    //   const inputData = JSON.parse(sessionStorage.formData)
    //   initialData = {
    //     ...initialData,
    //     ...inputData,
    //     paymentFrequency: inputData.paymentFrequency,
    //   }
    //   return initialData
    // }

    if (query.tier && query.tier !== 'custom') {
      const tier = page.donationTiers.find(tier => tier.id === query.tier);
      initialData = {
        amount: tier?.amount!,
        paymentFrequency: page.donationTiers.find(
          tier => tier.id === query.tier
        )?.recurrenceInterval!
      };
    } else {
      initialData = {
        // ...initialData,
        amount: closed || !allowCustomAmount ? 0 : (defaultTier?.amount ?? 0),
        paymentFrequency: defaultDonationRecurrenceInterval
      };
    }

    return initialData;
  };

  return (
    <Formik
      initialValues={getData()}
      validationSchema={schema}
      onSubmit={values => {
        sessionStorage.setItem('formData', JSON.stringify(values));
        onSubmit();
      }}
    >
      {({ values, setFieldValue }) => {
        const handleAmountChange = (value: number) => {
          setFieldValue('amount', value);
        };

        const selectedTier = useMemo(() => {
          return pageTiers.find(x => x.amount === values.amount);
        }, [values.amount, pageTiers]);

        return (
          <>
            <FocusError />
            <Form>
              <SelectDonationSectionWrapper>
                {isEmpty(pageTiers) ? (
                  <Text bold marginBottom="0.25rem">
                    Donation Amount
                  </Text>
                ) : (
                  <>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      flexDirection="row"
                      marginBottom={'1rem'}
                    >
                      <AmountButtonsWrapper>
                        {pageTiers.map((tier, index) => (
                          <AmountButton
                            isSelected={
                              pageTiers.some(i => i.amount == values.amount)
                                ? values.amount === tier.amount
                                : 0 == tier.amount
                            }
                            onClick={() => {
                              setFieldValue('amount', tier.amount);
                            }}
                            key={index}
                          >
                            {tier.id}
                          </AmountButton>
                        ))}
                      </AmountButtonsWrapper>
                      {showPaymentFrequency && (
                        <div className="w-[8.375rem] ml-4 max-md:hidden">
                          <SelectField
                            name="paymentFrequency"
                            options={frequencyOptions}
                          />
                        </div>
                      )}
                    </Box>
                    <AmountInputField
                      name="amount"
                      gridColumn={[null, '1 / 4']}
                      value={values.amount}
                      onChange={handleAmountChange}
                      autoFocus
                      disabled={!allowCustomAmount}
                    />
                  </>
                )}

                {page.pageFeatures.showTierDescription &&
                  selectedTier &&
                  selectedTier.description && (
                    <div className="text-sm p-4 border-2 border-primary rounded-sm mt-4">
                      <div className="font-medium text-xl mb-1 text-dark">
                        ${selectedTier.amount / 100}{' '}
                        <span className="capitalize">
                          {values.paymentFrequency ===
                          DonationRecurrenceInterval.None
                            ? 'One-Time'
                            : values.paymentFrequency}
                        </span>
                      </div>
                      <div>{selectedTier.description}</div>
                    </div>
                  )}

                {isProsperityRaffle && !selectedTier && (
                  <div className="text-sm p-4 border-2 border-primary rounded-sm mt-4">
                    <div className="font-medium text-xl mb-1 text-dark">
                      ${values.amount / 100}{' '}
                      <span className="capitalize">{'One-Time'}</span>
                    </div>
                    <div>Custom donation amount without raffle tickets.</div>
                  </div>
                )}

                {isEmpty(pageTiers) ? (
                  <Grid
                    gridTemplateColumns={['1fr', '1fr 1fr 1fr 1fr']}
                    gridColumnGap={16}
                  >
                    <AmountInputField
                      name="amount"
                      gridColumn={[null, '1 / 4']}
                      value={values.amount}
                      onChange={handleAmountChange}
                      autoFocus
                      disabled={!allowCustomAmount}
                    />
                    {showPaymentFrequency && (
                      <SelectField name="paymentFrequency" options={options} />
                    )}
                  </Grid>
                ) : (
                  <div className="w-full md:hidden">
                    {showPaymentFrequency && (
                      <SelectField name="paymentFrequency" options={options} />
                    )}
                  </div>
                )}

                {page.nonprofit.webId === WORKSPACE_IDS.RAWSTORY &&
                  values.amount >= 5000 &&
                  values.paymentFrequency !==
                    DonationRecurrenceInterval.None && (
                    <div className="text-sm p-3 border border-amber-400 bg-amber-50 rounded-sm text-amber-900 mt-4">
                      Please make sure you want to give ${values.amount / 100}{' '}
                      <span className="capitalize">
                        {values.paymentFrequency}
                      </span>
                      .
                    </div>
                  )}
              </SelectDonationSectionWrapper>
              <SubmitBtnWrapper>
                <Button
                  type="submit"
                  width="100%"
                  disabled={values.amount == 0 || closed}
                >
                  <ButtonBody>
                    {page.nonprofit.webId === WORKSPACE_IDS.THE_ADVOCATE ? ( // The Advocate adjustment
                      <>
                        {closed ? 'Campaign Has Ended' : 'Join by Credit Card'}
                      </>
                    ) : (
                      <>
                        <LockIcon />
                        {closed
                          ? 'Campaign Has Ended'
                          : 'Donate with Credit Card'}
                      </>
                    )}
                  </ButtonBody>
                </Button>
                {isUsingChariot && page.pageFeatures.chariotButton ? (
                  <ChariotConnect
                    cid={page.chariotConnectId!}
                    onDonationRequest={() => onDonationRequest(values.amount)}
                    theme="KindestTheme"
                    disabled={closed}
                    onSuccess={onSuccess}
                    onExit={onExit}
                  />
                ) : (
                  <></>
                )}
              </SubmitBtnWrapper>
            </Form>
          </>
        );
      }}
    </Formik>
  );
};
